import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import useTicketVisitorContact from 'flow/hooks/useTicketVisitorContact';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import {
    selectOriginDTOSubtype,
    selectVisitorDisplayName,
} from 'flow/selectors/TicketsSelectors';
import { ContactFull } from 'flow/types/Contact';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { getContactName } from 'flow/utils/contact-utils';
import { originContactIsPhoneNumber } from 'flow/utils/ticket-channel-utils';

interface Props {
    ticketKey: TicketKey;
}

export type SubtitlePart = { key: string; content: React.ReactNode };

export const buildSubtitleParts = (
    contact: TypedMap<ContactFull> | undefined,
    displayName: string | undefined,
    originDtoSubtype: string | undefined,
    ownCountryCode: string,
): SubtitlePart[] => {
    const fullName = getContactName(contact);
    const secondary =
        contact?.get('type') === 'bound'
            ? contact?.get('department')
            : contact?.get('companyName');
    const identifier = renderIdentifier(
        displayName,
        originDtoSubtype,
        ownCountryCode,
    );

    return [
        { key: 'name', content: fullName },
        { key: 'secondary', content: secondary },
        { key: 'identifier', content: identifier },
    ].filter((part) => Boolean(part.content));
};

const TicketHeaderSubtitle = ({ ticketKey }: Props) => {
    const { contact } = useTicketVisitorContact(ticketKey);

    const displayName = useAppSelector((state) =>
        selectVisitorDisplayName(state, ticketKey),
    );
    const originDtoSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const parts = buildSubtitleParts(
        contact,
        displayName,
        originDtoSubtype,
        ownCountryCode,
    );
    if (parts.length === 0) return null;

    return (
        <div className="line-clamp-1 text-sm break-all text-gray-500">
            {parts.map((part, index) => (
                <span key={`ths-${part.key}`}>
                    {index > 0 ? <span>{' • '}</span> : null}
                    <span>{part.content}</span>
                </span>
            ))}
        </div>
    );
};

const renderIdentifier = (
    displayName: string | undefined,
    originDtoSubtype: string | undefined,
    ownCountryCode: string,
) => {
    if (!displayName) return undefined;
    if (!originContactIsPhoneNumber(originDtoSubtype)) return displayName;
    const formatted = prettyFormatE164Number(displayName, ownCountryCode);
    return formatted ? <NumberWrapper>{formatted}</NumberWrapper> : displayName;
};

export default React.memo(TicketHeaderSubtitle);
